import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EngineeringIcon from "@mui/icons-material/Engineering";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import PersonIcon from "@mui/icons-material/Person";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import SchemaIcon from "@mui/icons-material/Schema";
import TimerIcon from "@mui/icons-material/Timer";
import baseApi from "../api/baseApi";
import { AccountTree } from "@mui/icons-material";
import { useTranslations, useOrganizations, useSelectedOrganization } from "hooks";
import { getTranslation } from "common";
import NotificationGroup from "./NotificationGroup";
import { Organization } from "model/organizationModel";

export interface NotificationConfig {
  id: number;
  idmessage: number;
  iddescribeusersettings: string;
  active: boolean;
  name: string;
}

export const CATEGORY_TO_SECTION_ID: Record<string, number> = {
  "NOTIFICATION.EDIT.GROUPS": 5,
  "NOTIFICATION.EDIT.ROLES": 3,
  "NOTIFICATION.EDIT.USERS": 4,
  "NOTIFICATION.EDIT.ORGANIZATION": 2,
  "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU": 1,
  "NOTIFICATION.EDIT.ROBOTS": 6,           // TODO: verify section ID from DB
  "NOTIFICATION.EDIT.PROCESSES_MODEL": 7,  // TODO: verify section ID from DB
  "NOTIFICATION.EDIT.SLA": 8,              // notification_messages.idsection = 8 (20261009_sla_notification_messages.sql)
};

export const IDDESCRIBE_TO_CATEGORY: Record<string, string> = {
  // Section 1 – Activities
  "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU":       "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESS_TOKENS_STATE_DOING":       "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESS_TOKENS_STATE_TODO":        "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESS_TOKENS_STATE_DONE":        "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESS_TOKENS_STATE_TODO_AGAIN":  "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESS_TOKENS_UPDATED":           "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESSES_INSERTED":               "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESSES_UPDATED":                "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESSES_ENDED":                  "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESSES_DELETED":                "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.ROBOTS_INSERTED":                  "NOTIFICATION.EDIT.ROBOTS",
  "NOTIFICATION.ROBOTS_DELETED":                   "NOTIFICATION.EDIT.ROBOTS",
  "NOTIFICATION.ROBOTS_UPDATED":                   "NOTIFICATION.EDIT.ROBOTS",
  "NOTIFICATION.PROCESSES_MODEL_INSERTED":         "NOTIFICATION.EDIT.PROCESSES_MODEL",
  "NOTIFICATION.PROCESSES_MODEL_UPDATED":          "NOTIFICATION.EDIT.PROCESSES_MODEL",
  "NOTIFICATION.PROCESSES_MODEL_PUBLISHED":        "NOTIFICATION.EDIT.PROCESSES_MODEL",
  "NOTIFICATION.PROCESSES_MODEL_DEPUBLISHED":      "NOTIFICATION.EDIT.PROCESSES_MODEL",
  "NOTIFICATION.PROCESSES_MODEL_DELETED":          "NOTIFICATION.EDIT.PROCESSES_MODEL",
  "NOTIFICATION.PROCESSES_TOKENS_INSERTED":        "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESSES_UPLOADS_INSERTED":       "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESSES_UPLOADS_UPDATED":        "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.PROCESSES_UPLOADS_DELETED":        "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.COMMENTS_INSERTED":               "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU",
  "NOTIFICATION.ROBOT_REMOVED_FROM_ORGANIZATION": "NOTIFICATION.EDIT.ROBOTS",
  // Section 2 – Organization
  "NOTIFICATION.EDIT.ORGANIZATION_UPDATED":                    "NOTIFICATION.EDIT.ORGANIZATION",
  "NOTIFICATION.ORGANIZATION_USERS_UPDATED_SET_ADMIN_FALSE":   "NOTIFICATION.EDIT.ORGANIZATION",
  "NOTIFICATION.ORGANIZATION_USERS_UPDATED_SET_ADMIN_TRUE":    "NOTIFICATION.EDIT.ORGANIZATION",
  "NOTIFICATION.USER_ABANDONED_ORGANIZATION":                  "NOTIFICATION.EDIT.ORGANIZATION",
  "NOTIFICATION.USER_REMOVED_FROM_ORGANIZATION":               "NOTIFICATION.EDIT.ORGANIZATION",
  "NOTIFICATION.ORGANIZATION_USERS_UPDATED_SET_ACTIVE":        "NOTIFICATION.EDIT.ORGANIZATION",
  // Section 3 – Roles
  "NOTIFICATION.EDIT.ROLES_UPDATED":        "NOTIFICATION.EDIT.ROLES",
  "NOTIFICATION.EDIT.ROLES_DELETED":        "NOTIFICATION.EDIT.ROLES",
  "NOTIFICATION.EDIT.ROLES_INSERTED":       "NOTIFICATION.EDIT.ROLES",
  "NOTIFICATION.EDIT.USERS_ROLES_INSERTED": "NOTIFICATION.EDIT.ROLES",
  "NOTIFICATION.EDIT.USERS_ROLES_DELETED":  "NOTIFICATION.EDIT.ROLES",
  // Section 4 – Users
  "NOTIFICATION.EDIT.USERS_GROUPS_INSERTED":       "NOTIFICATION.EDIT.USERS",
  "NOTIFICATION.EDIT.USERS_GROUPS_DELETED":        "NOTIFICATION.EDIT.USERS",
  "NOTIFICATION.EDIT.USERS_GROUPS_UPDATED":        "NOTIFICATION.EDIT.USERS",
  "NOTIFICATION.USERS_GROUPS_UPDATED_SET_HOD_TRUE":  "NOTIFICATION.EDIT.USERS",
  "NOTIFICATION.USERS_GROUPS_UPDATED_SET_HOD_FALSE": "NOTIFICATION.EDIT.USERS",
  // Section 5 – Groups
  "NOTIFICATION.EDIT.GROUPS_UPDATED":  "NOTIFICATION.EDIT.GROUPS",
  "NOTIFICATION.EDIT.GROUPS_INSERTED": "NOTIFICATION.EDIT.GROUPS",
  "NOTIFICATION.EDIT.GROUPS_DELETED":  "NOTIFICATION.EDIT.GROUPS",
  // Section 8 – SLA (NOTIFICATION.SLA_NO_RECIPIENT is internal: not user-configurable, not listed)
  "NOTIFICATION.SLA_AT_RISK":           "NOTIFICATION.EDIT.SLA",
  "NOTIFICATION.SLA_BREACHED":          "NOTIFICATION.EDIT.SLA",
  "NOTIFICATION.SLA_NUDGE":             "NOTIFICATION.EDIT.SLA",
  "NOTIFICATION.SLA_ESCALATION":        "NOTIFICATION.EDIT.SLA",
  "NOTIFICATION.SLA_BREACH_AT_CLOSURE": "NOTIFICATION.EDIT.SLA",
  "NOTIFICATION.SLA_DIGEST":            "NOTIFICATION.EDIT.SLA",
};

const NotificationsSettings: React.FC = () => {
  const CATEGORY_MAP: Record<string, { label: string; icon: JSX.Element }> = {
    "NOTIFICATION.EDIT.GROUPS": { label: 'generic_group_notifications', icon: <GroupsIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.ROLES": { label: "generic_notification.roles", icon: <EngineeringIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.USERS": { label: "generic_notification.users", icon: <PersonIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.ORGANIZATION": { label: "generic_notification.organization", icon: <GroupWorkIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.NEW_ACTIVITY_FOR_YOU": { label: "generic_notification.activities", icon: <AccountTree sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.ROBOTS": { label: "generic_notification.robots", icon: <PrecisionManufacturingIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.PROCESSES_MODEL": { label: "generic_notification.process_models", icon: <SchemaIcon sx={{ color: "primary.main" }} /> },
    "NOTIFICATION.EDIT.SLA": { label: "generic_notification.sla", icon: <TimerIcon sx={{ color: "primary.main" }} /> },
  };

  const CHANNEL_IDS: Record<string, number> = {
    "Push": 1,
    "Email": 2,
  };

  const [notificationsConfig, setNotificationsConfig] = useState<NotificationConfig[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [allOrganizations, setAllOrganizations] = useState(false);
  const [selectedNotificationOrganizationId, setSelectedNotificationOrganizationId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [organizations] = useOrganizations();
  const selectedOrganization = useSelectedOrganization();
  const translations = useTranslations()

  useEffect(() => {
    if (selectedOrganization?.id) {
      setSelectedNotificationOrganizationId(selectedOrganization.id);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const url = allOrganizations || !selectedNotificationOrganizationId
          ? "/notifications/configuration"
          : `/notifications/configuration/${selectedNotificationOrganizationId}`;
        const response = await baseApi.get(url);
        setNotificationsConfig(response.data);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedNotificationOrganizationId, allOrganizations]);

  const handleSectionToggle = async (categoryPrefix: string, channelName: string, nextActive: boolean) => {
    const idsection = CATEGORY_TO_SECTION_ID[categoryPrefix];
    const idchannel = CHANNEL_IDS[channelName];
    if (!idsection || !idchannel) return;

    try {
      const payload: Record<string, unknown> = {
        idsection,
        idchannel,
        active: nextActive,
        all_organizations: allOrganizations,
      };
      if (!allOrganizations && selectedNotificationOrganizationId) {
        payload.idorganization = selectedNotificationOrganizationId;
      }
      await baseApi.post("notifications/configuration/section", payload);

      setNotificationsConfig(prev =>
        prev.map(notification => {
          if (
            IDDESCRIBE_TO_CATEGORY[notification.iddescribeusersettings] === categoryPrefix &&
            notification.name === channelName
          ) {
            return { ...notification, active: nextActive };
          }
          return notification;
        })
      );
    } catch (err) {}
  };

  const handleActiveToggle = async (idmessage: number, channelName: string, isActive: boolean) => {
    const idchannel = CHANNEL_IDS[channelName];
    if (!idchannel) return;

    try {
      const payload: Record<string, unknown> = {
        idmessage,
        idchannel,
        active: !isActive,
        all_organizations: allOrganizations,
      };

      if (!allOrganizations && selectedNotificationOrganizationId) {
        payload.idorganization = selectedNotificationOrganizationId;
      }

      await baseApi.post("notifications/configuration/message", payload);

      setNotificationsConfig(prev =>
        prev.map(notification => {
          if (notification.idmessage === idmessage && notification.name === channelName) {
            return { ...notification, active: !isActive };
          }
          return notification;
        })
      );
    } catch (err) {
    }
  };

  const groupedNotifications: Record<string, NotificationConfig[]> = useMemo(() => {
    const _groupedNotifications: Record<string, NotificationConfig[]> = {};
    notificationsConfig?.forEach(notification => {
      const matchingPrefix = IDDESCRIBE_TO_CATEGORY[notification.iddescribeusersettings];
      if (matchingPrefix) {
        const categoryLabel = CATEGORY_MAP[matchingPrefix].label;
        if (!_groupedNotifications[categoryLabel]) {
          _groupedNotifications[categoryLabel] = [];
        }
        _groupedNotifications[categoryLabel].push(notification);
      }
    });
    return _groupedNotifications;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsConfig]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !(prev[category] ?? true),
    }));
  };

  return (
    <React.Fragment>
      <Typography variant="h4" sx={{ mb: 4, mt: 8, ml: 4, textAlign: "center" }}>
        {getTranslation(translations, 'generic.notifications.settings')}
      </Typography>
      <Box sx={{ width: "55%", ml: "auto", mr: "auto", mb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              visibility: allOrganizations ? "hidden" : "visible",
              pointerEvents: allOrganizations ? "none" : "auto",
            }}
          >
            <Typography variant="body2" noWrap>
              {getTranslation(translations, "generic.select_organization")}:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={selectedNotificationOrganizationId ?? ""}
                onChange={(e) => setSelectedNotificationOrganizationId(e.target.value as number)}
                displayEmpty
              >
                {organizations.map((org: Organization) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={allOrganizations}
                onChange={(e) => setAllOrganizations(e.target.checked)}
              />
            }
            label={getTranslation(translations, "generic.apply_to_all_organizations")}
            sx={{ whiteSpace: "nowrap", mr: 0 }}
          />
        </Box>
        <TextField
          size="small"
          placeholder={getTranslation(translations, "generic.search.notification")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: "100%", "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Grid container spacing={2} sx={{ width: "55%", ml: "auto", mr: "auto", mb: 5 }}>
        {Object.entries(groupedNotifications).map(([labelKey, notifications]) => {
          const categoryKey = IDDESCRIBE_TO_CATEGORY[notifications[0].iddescribeusersettings]
          return (
            <NotificationGroup
              key={`${labelKey}-${selectedNotificationOrganizationId ?? 'all'}-${allOrganizations}`}
              category={getTranslation(translations, labelKey)}
              categoryKey={categoryKey as string}
              expandedCategories={expandedCategories}
              handleActiveToggle={handleActiveToggle}
              handleSectionToggle={handleSectionToggle}
              notifications={notifications}
              toggleCategory={toggleCategory}
              categoryIcon={categoryKey ? CATEGORY_MAP[categoryKey].icon : ""}
              searchTerm={searchTerm}
            />
          );
        })}
      </Grid>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default NotificationsSettings;
